/* global document */
import { useRef, useLayoutEffect } from 'react';

import isScrollbarClick from '../isScrollbarClick/index.ts';
import useEventCallback from '../useEventCallback/index.ts';

export { withClickOutside } from './withClickOutside.tsx';

export type ClickOutsideEvent = MouseEvent | PointerEvent | TouchEvent;

export type MouseEventType =
  | 'click'
  | 'dblclick'
  | 'contextmenu'
  | 'mousedown'
  | 'mouseenter'
  | 'mouseleave'
  | 'mousemove'
  | 'mouseout'
  | 'mouseover'
  | 'mouseup'
  | 'pointerdown'
  | 'pointerup'
  | 'touchstart'
  | 'touchend';

export interface UseClickOutsideOptions {
  /**
   * Whether clicking outside is actively listening (for performance)
   */
  active?: boolean;
  /**
   * Exclude clicks upon the scrollbar from being registered as a click
   */
  excludeScrollbar?: boolean;
  /**
   * ClassName(s) that won't register as a click outside
   */
  ignoreClassName?: string | string[];
  /**
   * Event types to listen to on an element
   */
  eventTypes?: MouseEventType[];
  /**
   * Whether to `stopPropagation` when handling the event bubbling
   */
  stopPropagation?: boolean;
  /**
   * Whether to `preventDefault` when handling the event bubbling
   */
  preventDefault?: boolean;
}

function findHighest(
  current: Element,
  componentNode: Element,
  ignoreClass: string | string[]
): Element | Document {
  const classesToIgnore = new Array<string>().concat(ignoreClass);

  /*
   * Adapted from: https://github.com/Pomax/react-onclickoutside/blob/master/src/index.js
   */
  if (
    current === componentNode ||
    (current?.classList &&
      classesToIgnore.some(c => {
        return current.classList.contains(c);
      }))
  ) {
    return componentNode; // Return the component node to indicate "clicked inside"
  }

  if (current.parentNode) {
    if (current.parentNode instanceof Element) {
      return findHighest(current.parentNode, componentNode, ignoreClass);
    } else if (current.parentNode === document) {
      return document; // We've reached the document level
    }
  }

  // If parentNode is null, the element is detached from the DOM
  // This should be considered as "clicked outside"
  return document;
}

// Removed isClickedOutside function - logic moved to coordinator
const DEFAULT_EVENT_TYPES: MouseEventType[] = ['pointerdown'];
const DEFAULT_IGNORE_CLASSNAMES = ['aglmd-ignore-click-outside', 'ignore-react-onclickoutside'];

// Global coordination system for nested useClickOutside hooks
interface ClickOutsideHandler {
  element: Element;
  handler: (e: ClickOutsideEvent) => void;
  stopPropagation: boolean;
  preventDefault: boolean;
  excludeScrollbar: boolean;
  ignoreClassName: string | string[];
}

class ClickOutsideCoordinator {
  private handlers = new Map<Element, ClickOutsideHandler>();
  private isListening = false;
  private eventTypes: MouseEventType[] = ['pointerdown'];

  private getElementDepth(element: Element): number {
    let depth = 0;
    let current = element.parentElement;
    while (current) {
      depth++;
      current = current.parentElement;
    }
    return depth;
  }

  private isClickedOutside(
    e: ClickOutsideEvent,
    element: Element,
    ignoreClassName: string | string[]
  ): boolean {
    // Check if e.target is null or not an Element
    if (!e.target || !(e.target instanceof Element)) {
      return true; // Consider it outside if target is null or not an Element
    }

    const result = findHighest(e.target, element, ignoreClassName);
    // findHighest returns the component node if clicked inside or on ignored element
    // findHighest returns document if clicked outside
    return result === document;
  }

  private handleGlobalClick = (e: ClickOutsideEvent) => {
    // Sort handlers by DOM hierarchy (inner elements first)
    const sortedHandlers = Array.from(this.handlers.values()).sort((a, b) => {
      // Inner elements (higher depth) should execute first
      return this.getElementDepth(b.element) - this.getElementDepth(a.element);
    });

    for (const handlerInfo of sortedHandlers) {
      // Check scrollbar exclusion first
      if (handlerInfo.excludeScrollbar && isScrollbarClick(e)) {
        continue; // Skip this handler if it excludes scrollbar clicks
      }

      if (this.isClickedOutside(e, handlerInfo.element, handlerInfo.ignoreClassName)) {
        // Call preventDefault if requested
        if (handlerInfo.preventDefault) {
          e.preventDefault();
        }

        // Call stopPropagation if requested
        if (handlerInfo.stopPropagation) {
          e.stopPropagation();
        }

        // Execute the handler
        handlerInfo.handler(e);

        // If this handler has stopPropagation, don't call outer handlers
        if (handlerInfo.stopPropagation) {
          break;
        }
      }
    }
  };

  register(
    element: Element,
    handler: (e: ClickOutsideEvent) => void,
    options: {
      stopPropagation: boolean;
      preventDefault: boolean;
      excludeScrollbar: boolean;
      ignoreClassName: string | string[];
    }
  ) {
    this.handlers.set(element, {
      element,
      handler,
      stopPropagation: options.stopPropagation,
      preventDefault: options.preventDefault,
      excludeScrollbar: options.excludeScrollbar,
      ignoreClassName: options.ignoreClassName
    });

    // Start listening if this is the first handler
    if (!this.isListening) {
      this.startListening();
    }
  }

  unregister(element: Element) {
    this.handlers.delete(element);

    // Stop listening if no more handlers
    if (this.handlers.size === 0) {
      this.stopListening();
    }
  }

  private startListening() {
    if (this.isListening) {
      return;
    }

    this.isListening = true;
    this.eventTypes.forEach(eventType => {
      document.addEventListener(eventType, this.handleGlobalClick, {
        capture: true,
        passive: true
      } as AddEventListenerOptions);
    });
  }

  private stopListening() {
    if (!this.isListening) {
      return;
    }

    this.isListening = false;
    this.eventTypes.forEach(eventType => {
      document.removeEventListener(eventType, this.handleGlobalClick, {
        capture: true,
        passive: true
      } as AddEventListenerOptions);
    });
  }

  updateEventTypes(eventTypes: MouseEventType[]) {
    if (this.isListening) {
      // Remove old listeners
      this.eventTypes.forEach(eventType => {
        document.removeEventListener(eventType, this.handleGlobalClick, {
          capture: true,
          passive: true
        } as AddEventListenerOptions);
      });
    }

    // Update event types to the new ones
    this.eventTypes = [...eventTypes];

    if (this.isListening) {
      // Add new listeners
      this.eventTypes.forEach(eventType => {
        document.addEventListener(eventType, this.handleGlobalClick, {
          capture: true,
          passive: true
        } as AddEventListenerOptions);
      });
    }
  }
}

// Global coordinator instance
const coordinator = new ClickOutsideCoordinator();

/**
 * A hook that detects whether an element was clicked outside of.
 * If so it will call the provided handler function
 *
 * @param {Function} onClickOutsideFn The function to call when clicked outside
 * @param {Object} options options to pass to the hook
 * @param {boolean} options.active Whether clicking outside is actively listening (for performance)
 * @param {boolean} options.excludeScrollbar Whether clicking outside is excluding clicks on the scrollbar
 * @param {string | string[]} options.ignoreClassName ClassName(s) that won't register as a click outside
 * @param {MouseEventType[]} options.eventTypes Event types to listen to on an element
 * @param {boolean} options.stopPropagation Whether to `stopPropagation` when handling the event bubbling
 * @param {boolean} options.preventDefault Whether to `preventDefault` when handling the event bubbling
 * @returns {React.MutableRefObject<Element | null>} nodeRef The DOM node to listen to for clicks outside of
 * @example <caption>Example Usage</caption>
 * const ref = useClickOutside(() => {}, {
 *  active: isActive,
 *  ignoreClassName: 'ignore-class',
 *  excludeScrollbar: true
 * });
 * <div ref={ref}>Click Outside</div>
 */
export function useClickOutside<T extends HTMLElement>(
  onClickOutsideFn: (e: ClickOutsideEvent) => void,
  {
    active = true,
    excludeScrollbar = false,
    ignoreClassName,
    eventTypes = DEFAULT_EVENT_TYPES,
    stopPropagation = false,
    preventDefault = false
  }: UseClickOutsideOptions = {}
): React.MutableRefObject<T | null> {
  const node = useRef<T | null>(null);
  const handlerRef = useRef<((e: ClickOutsideEvent) => void) | undefined>(undefined);

  // Create a stable handler reference
  handlerRef.current = useEventCallback((e: ClickOutsideEvent) => {
    onClickOutsideFn(e);
  });

  useLayoutEffect(() => {
    const element = node.current;

    // If not active, unregister any existing element and return
    if (!active) {
      if (element) {
        coordinator.unregister(element);
      }

      return;
    }

    // Concatenate custom ignoreClassName with defaults
    const combinedIgnoreClassName = [
      ...DEFAULT_IGNORE_CLASSNAMES,
      ...([] as string[]).concat(ignoreClassName ?? '')
    ];

    // Track the element that gets registered so we can unregister it properly
    let registeredElement: Element | null = null;

    // Deferred attachment to prevent popups from closing immediately after opening
    const timeoutId = setTimeout(() => {
      // Get fresh element reference after timeout
      const currentElement = node.current;

      // Only register if we have a valid element
      if (currentElement) {
        coordinator.register(currentElement, handlerRef.current!, {
          stopPropagation,
          preventDefault,
          excludeScrollbar,
          ignoreClassName: combinedIgnoreClassName
        });

        // Track the element that was actually registered
        registeredElement = currentElement;

        // Update event types if they've changed
        const events = ([] as MouseEventType[]).concat(eventTypes);
        coordinator.updateEventTypes(events);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);

      // Unregister the element that was actually registered, not the one captured at effect start
      if (registeredElement) {
        coordinator.unregister(registeredElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    eventTypes,
    excludeScrollbar,
    ignoreClassName,
    preventDefault,
    stopPropagation,
    node.current
  ]);

  return node;
}
